import type { Guide } from "../types";

// ── Apps: Basic / Live Feeds / Communication ─────────────────────────────────
// One guide per store app in these three groups. Every app follows the same
// flow as the "add-an-app" overview — open the App Store, pick the app,
// configure ITS settings, save & name, then assign to a screen. The configure
// step is tailored to each app's own editor (labels checked against admin-web).

// A reusable final step: save the app and assign it to a screen.
const assignStep = {
  title: "Save it and put it on a screen",
  blocks: [
    {
      type: "p" as const,
      text: "Give the app a clear App name and click Save Changes. It’s saved to the My Apps tab, where Edit reopens it later. Then assign it to a screen the same way as any content — with Set content on the screen, or by adding it to a playlist.",
    },
    {
      type: "steps-ref" as const,
      label: "See: Assign content to a screen",
      to: "/screens/assign-content",
    },
  ],
};

// Connectivity notes — mirror the note each editor shows (connectivity.* in admin-web).
const offlineNote = {
  type: "note" as const,
  tone: "info" as const,
  text: "Works offline: the app plays from its saved settings, so the screen keeps showing it even with no internet connection.",
};
const cachedNote = {
  type: "note" as const,
  tone: "info" as const,
  text: "Works offline with the last synced data: the app needs a connection to refresh, and while the screen is offline it keeps showing the most recent data it downloaded.",
};
const onlineNote = {
  type: "note" as const,
  tone: "warning" as const,
  text: "Needs internet: this app shows live content from an outside service. It stops updating — and may go blank — while the screen is offline.",
};

// The date/time apps share one timezone behaviour.
const autoTzNote = {
  type: "note" as const,
  tone: "tip" as const,
  text: "Leave Timezone set to auto and the app follows each screen’s own timezone, so one saved app shows the right local time on screens in different cities. Type an IANA zone instead (for example Europe/London or America/New_York) to pin it to one place.",
};

export const appsCoreGuides: Guide[] = [
  // ── Basic ──────────────────────────────────────────────────────────────────
  {
    slug: "website-url",
    category: "apps",
    route: "/apps/website-url",
    title: "Website URL",
    summary: "Display any web page full-screen on a screen.",
    intro:
      "The Website URL app shows a web page as full-screen signage — a dashboard, a live status page, a booking calendar, or your own site. It can reload on a schedule so the display always shows the latest version.",
    readingTime: 3,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Website URL is in the Basic category — click the Basic chip or search for it.",
          },
        ],
        screenshot: {
          src: "apps/website-url/01.png",
          alt: "The App Store filtered to the Website URL app",
          todo: "App Store tab, search “Website URL”, Website URL card visible.",
        },
      },
      {
        title: "Enter the address and choose how it displays",
        blocks: [
          {
            type: "p",
            text: "Click Website URL. Type or paste the address into Website URL (https:// is added for you if you leave it out) and use Test URL to open it in a new tab. Then set:",
          },
          {
            type: "ul",
            items: [
              "Fit mode — Contain, Cover or Stretch.",
              "Render mode — Direct iframe loads the page straight into the screen; Proxy mode fetches it through PulsePanels first.",
              "Auto refresh — Off, or reload every 1, 5, 15 or 30 minutes.",
            ],
          },
          onlineNote,
          {
            type: "note",
            tone: "tip",
            text: "For a long page that should scroll slowly so nothing below the fold is missed, use the Page Scroll app instead.",
          },
        ],
        screenshot: {
          src: "apps/website-url/02.png",
          alt: "The Website URL editor with Website URL, Fit mode, Render mode and Auto refresh",
          caption: "Enter the address, then pick Fit mode, Render mode and Auto refresh.",
          todo: "Configure: Website URL editor with the “Needs internet” note, Website URL, Fit mode, Render mode, Auto refresh and the Runtime behavior box, preview on the right.",
        },
      },
      {
        title: "If the preview says the site does not allow embedding",
        blocks: [
          {
            type: "p",
            text: "Many sites refuse to be shown inside another page. When that happens in Direct iframe mode, the preview shows “This site does not allow embedding” instead of a blank frame — a screen would show the same blank frame. Click Switch to Proxy mode: PulsePanels fetches the page on the server instead, which works around the block for most sites.",
          },
          {
            type: "note",
            tone: "info",
            text: "If a site still won’t display in Proxy mode, try its public or “kiosk” link, or use the Embed app with the embed code the service provides.",
          },
        ],
        screenshot: {
          src: "apps/website-url/03.png",
          alt: "The Website URL preview explaining that the site does not allow embedding, with a Switch to Proxy mode button",
          caption: "A blocked site is explained, with a one-click switch to Proxy mode.",
          todo: "Website URL editor, Render mode = Direct iframe, URL of a site that sends X-Frame-Options (e.g. https://github.com). Capture the “This site does not allow embedding” card with the Switch to Proxy mode button.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Embed", to: "/apps/embed" },
      { label: "How apps work", to: "/apps/add-an-app" },
    ],
    keywords: ["website", "url", "web page", "iframe", "proxy", "blocked", "x-frame-options", "dashboard", "kiosk"],
  },

  {
    slug: "clock",
    category: "apps",
    route: "/apps/clock",
    title: "Clock",
    summary: "Show a digital or analog clock.",
    intro:
      "The Clock app puts a clean, always-accurate clock on any screen — perfect for lobbies, offices, and studios. Choose the style, format, colours, and time zone that suit the space.",
    readingTime: 2,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Clock is in the Basic category.",
          },
        ],
        screenshot: {
          src: "apps/clock/01.png",
          alt: "The App Store filtered to the Clock app",
          todo: "App Store tab, search “Clock”, Clock card visible.",
        },
      },
      {
        title: "Set the time zone and style",
        blocks: [
          {
            type: "p",
            text: "Click Clock. Add an optional Location label (shown with the time), then set the Timezone. Choose the Clock format (24-hour or 12-hour), the Visual style (Digital or Analog), a Background (Black, White, or Transparent to show the layer behind) and a Font color. Finally decide whether to show Seconds, the Date, and the AM / PM indicator.",
          },
          autoTzNote,
          offlineNote,
        ],
        screenshot: {
          src: "apps/clock/02.png",
          alt: "The Clock editor with Location label, Timezone, Clock format, Visual style and colour options",
          caption: "Timezone, format, style, colours, and what to show.",
          todo: "Configure: Clock editor for a NEW clock, so Timezone shows “auto” and the hint “auto = follow the screen’s timezone…”; “Works offline” note visible; preview on the right.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "World Clock", to: "/apps/world-clock" },
      { label: "Date", to: "/apps/date" },
    ],
    keywords: ["clock", "time", "analog", "digital", "24 hour", "timezone", "auto"],
  },

  {
    slug: "world-clock",
    category: "apps",
    route: "/apps/world-clock",
    title: "World Clock",
    summary: "Show the time in up to six cities at once.",
    intro:
      "The World Clock app displays the current time in several cities at once — ideal for global teams, trading floors, and travel lobbies. Add the cities you care about and choose a classic grid or the premium world-map theme.",
    readingTime: 3,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. World Clock is in the Basic category.",
          },
        ],
        screenshot: {
          src: "apps/world-clock/01.png",
          alt: "The App Store filtered to the World Clock app",
          todo: "App Store tab, search “World Clock”, World Clock card visible.",
        },
      },
      {
        title: "Pick a theme and add cities",
        blocks: [
          {
            type: "p",
            text: "Click World Clock. Add an optional Heading, then choose a Theme:",
          },
          {
            type: "ul",
            items: [
              "Classic — grid of clocks: add up to six cities with Add city, reorder them by dragging or with the arrows, and set Clock style, Time format, Columns, Seconds, Date, AM / PM, Background and Font color.",
              "Premium — world map, markers & landmarks: search and add up to six cities (each gets a landmark automatically), then set Clock display (Mixed, Analog only or Digital only), Columns, Orientation, Time format, World map (Minimal, Dotted or None), Glowing map markers and Landmarks.",
            ],
          },
          offlineNote,
        ],
        screenshot: {
          src: "apps/world-clock/02.png",
          alt: "The World Clock editor with the Classic theme and four cities",
          caption: "Choose Classic or Premium, then add up to six cities.",
          todo: "World Clock editor, Classic theme, 4 cities listed, Add city search open, preview on the right.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Clock", to: "/apps/clock" },
      { label: "Date", to: "/apps/date" },
    ],
    keywords: ["world clock", "time zones", "cities", "global", "premium theme", "world map", "landmarks"],
  },

  {
    slug: "countdown-timer",
    category: "apps",
    route: "/apps/countdown-timer",
    title: "Count Down Timer",
    summary: "Count down to an event, launch, or deadline.",
    intro:
      "The Count Down Timer app counts down to a moment that matters — a product launch, an opening, a big game, or a deadline — building anticipation as the days, hours, minutes and seconds tick toward zero.",
    readingTime: 2,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Count Down Timer is in the Basic category.",
          },
        ],
        screenshot: {
          src: "apps/countdown-timer/01.png",
          alt: "The App Store filtered to the Count Down Timer app",
          todo: "App Store tab, search “Count Down Timer”, card visible.",
        },
      },
      {
        title: "Set the target and the message at zero",
        blocks: [
          {
            type: "p",
            text: "Click Count Down Timer. Add a Title shown above the timer (for example “Grand opening in”) or leave it blank to hide it. Pick the Target date & time and its Timezone, choose whether to show Seconds, and type the message to show When it reaches zero (for example “We’re open!”). Then pick a Background — Black, White, Custom color, Background image (from your media library) or Transparent — and a Font color.",
          },
          autoTzNote,
          offlineNote,
        ],
        screenshot: {
          src: "apps/countdown-timer/02.png",
          alt: "The Count Down Timer editor with Title, Target date & time, Timezone and When it reaches zero",
          caption: "Pick the target moment, its timezone, and what shows at zero.",
          todo: "Configure: Countdown editor for a NEW timer, so Timezone shows “auto” with the “auto = follow the screen’s timezone” hint; Background dropdown showing the five options if possible.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Count Up Timer", to: "/apps/countup-timer" },
      { label: "Date", to: "/apps/date" },
    ],
    keywords: ["countdown", "count down", "timer", "event", "launch", "deadline", "days left"],
  },

  {
    slug: "countup-timer",
    category: "apps",
    route: "/apps/countup-timer",
    title: "Count Up Timer",
    summary: "Count up from a start date — days-since, streaks, milestones.",
    intro:
      "The Count Up Timer app counts forward from a moment you choose — time since an opening, days without an incident, or a running streak — a simple way to show ongoing progress.",
    readingTime: 2,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Count Up Timer is in the Basic category.",
          },
        ],
        screenshot: {
          src: "apps/countup-timer/01.png",
          alt: "The App Store filtered to the Count Up Timer app",
          todo: "App Store tab, search “Count Up Timer”, card visible.",
        },
      },
      {
        title: "Set the start and the look",
        blocks: [
          {
            type: "p",
            text: "Click Count Up Timer. Add a Title shown above the timer (for example “Days since opening”) or leave it blank to hide it. Set the Start date & time and its Timezone, and choose whether to show Seconds. The timer shows days, hours and minutes counting up from the start. Then pick a Background (Black, White, Custom color, Background image or Transparent) and a Font color.",
          },
          autoTzNote,
          offlineNote,
        ],
        screenshot: {
          src: "apps/countup-timer/02.png",
          alt: "The Count Up Timer editor with Title, Start date & time, Timezone and Seconds",
          caption: "Set the start moment, its timezone, and the look.",
          todo: "Configure: Count Up editor for a NEW timer, so Timezone shows “auto” with the “auto = follow the screen’s timezone” hint; preview on the right.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Count Down Timer", to: "/apps/countdown-timer" },
      { label: "Date", to: "/apps/date" },
    ],
    keywords: ["count up", "count-up", "timer", "days since", "streak", "milestone", "elapsed"],
  },

  {
    slug: "date",
    category: "apps",
    route: "/apps/date",
    title: "Date",
    summary: "Display today’s date in a clean layout.",
    intro:
      "The Date app shows today’s date in a crisp, readable layout — stacked, as a big day number, or as a small calendar. Pair it with the Clock or Weather app, or use it on its own.",
    readingTime: 2,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Date is in the Basic category.",
          },
        ],
        screenshot: {
          src: "apps/date/01.png",
          alt: "The App Store search results for “Date”",
          todo: "App Store tab, search “Date”, Date card visible.",
        },
      },
      {
        title: "Choose the layout and format",
        blocks: [
          {
            type: "p",
            text: "Click Date. Add an optional Title shown above the date (for example “Today”) and set the Timezone. Then choose:",
          },
          {
            type: "ul",
            items: [
              "Layout — Stacked (weekday + date), Big day number, or Calendar (month grid + day). The Calendar layout adds an Accent color.",
              "Date format — for example 24 June 2027, June 24, 2027, 24 Jun 2027, or 24/06/2027.",
              "Weekday — show or hide the day name.",
              "Background (Black, White, Custom color, Background image or Transparent) and Font color.",
            ],
          },
          autoTzNote,
          offlineNote,
        ],
        screenshot: {
          src: "apps/date/02.png",
          alt: "The Date editor with Title, Timezone, Layout, Date format and Weekday",
          caption: "Pick a layout, a date format, and the look.",
          todo: "Configure: Date editor for a NEW app, so Timezone shows “auto” with the “auto = follow the screen’s timezone” hint; Layout = Stacked; preview on the right.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Clock", to: "/apps/clock" },
      { label: "World Clock", to: "/apps/world-clock" },
    ],
    keywords: ["date", "today", "calendar", "day", "format", "weekday"],
  },

  {
    slug: "embed",
    category: "apps",
    route: "/apps/embed",
    title: "Embed",
    summary: "Embed an external widget by URL or embed code.",
    intro:
      "The Embed app puts an external widget straight onto a screen using its embed URL or embed code — booking widgets, maps, calendars, live charts, social embeds, or anything a service gives you as embeddable code.",
    readingTime: 2,
    status: "draft",
    group: "Basic",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Embed is in the Basic category.",
          },
        ],
        screenshot: {
          src: "apps/embed/01.png",
          alt: "The App Store search results for “Embed”",
          todo: "App Store tab, search “Embed”, Embed card visible.",
        },
      },
      {
        title: "Add the URL or embed code",
        blocks: [
          {
            type: "p",
            text: "Click Embed and choose the Source: Website URL for an embeddable page address, or Embed code to paste the HTML or iframe snippet the service gave you (Google Calendar, Maps, Power BI, social embeds and so on). Then pick how it fills the zone with Fit — Stretch to fill, Fit (letterbox), or Fill (crop).",
          },
          onlineNote,
          {
            type: "note",
            tone: "tip",
            text: "If you only have a normal web-page address, the Website URL app is usually the better fit — it also explains when a site blocks embedding.",
          },
        ],
        screenshot: {
          src: "apps/embed/02.png",
          alt: "The Embed editor with Source, URL and Fit",
          caption: "Choose Website URL or Embed code, then how it fits.",
          todo: "Embed editor: Source, URL (or Embed code textarea), Fit, preview on the right.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Website URL", to: "/apps/website-url" },
      { label: "How apps work", to: "/apps/add-an-app" },
    ],
    keywords: ["embed", "iframe", "widget", "snippet", "embed code", "external"],
  },

  // ── Live Feeds ─────────────────────────────────────────────────────────────
  {
    slug: "weather",
    category: "apps",
    route: "/apps/weather",
    title: "Weather",
    summary: "Current conditions and a 7-day forecast for any location.",
    intro:
      "The Weather app shows live conditions and a 7-day forecast with animated backgrounds for any location, updating on its own throughout the day — a friendly, useful touch for lobbies, cafés, and workplaces.",
    readingTime: 2,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Weather is in the Live Feeds category.",
          },
        ],
        screenshot: {
          src: "apps/weather/01.png",
          alt: "The App Store filtered to the Weather app",
          todo: "App Store tab, search “Weather”, Weather card visible.",
        },
      },
      {
        title: "Pick the location and units",
        blocks: [
          {
            type: "p",
            text: "Click Weather. Under City / Location, start typing an address, area or postcode and pick a suggestion — or click anywhere on the map to drop the pin. The Display name (shown on screen) fills in from the city; change it if you want a shorter label. Then set the Temperature unit (°C or °F), the Clock format, the Display mode (7-day forecast + current, or Today only) and a Background (PulsePanels blue, Transparent, Solid color or an image).",
          },
          cachedNote,
        ],
        screenshot: {
          src: "apps/weather/02.png",
          alt: "The Weather editor with City / Location search and map, units and display mode",
          caption: "Search or click the map to set the location, then pick units and display mode.",
          todo: "Configure: Weather editor with the connectivity note visible, a city selected (London — map pin + preview rendering), Temperature unit, Clock format, Display mode, Background. Needs a Google Maps key that allows the capture host as a referrer (the local demo's key refuses helpdemo.localhost and localhost with RefererNotAllowedMapError, so it is not captured yet); do not centre the map on Doha.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Clock", to: "/apps/clock" },
      { label: "Date", to: "/apps/date" },
    ],
    keywords: ["weather", "forecast", "temperature", "conditions", "celsius", "fahrenheit", "7-day"],
  },

  {
    slug: "rss-feed",
    category: "apps",
    route: "/apps/rss-feed",
    title: "RSS Feed & news apps",
    summary: "Show headlines from any RSS feed, or from a ready-made news app.",
    intro:
      "The RSS Feed app turns any RSS source into rotating headline cards or a scrolling ticker — a news site, a blog, a company feed, or an internal announcements feed. The store’s news apps (Headline News, Business & Finance News, Technology News, Entertainment News, Natural World News and Sports News) use the same editor with a curated list of sources, so you don’t have to hunt for a feed address.",
    readingTime: 3,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. RSS Feed and the six news apps are in the Live Feeds category. Pick RSS Feed to use your own feed, or a news app to start from a curated source.",
          },
        ],
        screenshot: {
          src: "apps/rss-feed/01.png",
          alt: "The App Store filtered to the RSS Feed app",
          todo: "App Store tab, Live Feeds chip selected, showing RSS Feed and the six news apps (Headline News … Sports News).",
        },
      },
      {
        title: "Choose the feed",
        blocks: [
          {
            type: "p",
            text: "Give it a Feed name. For RSS Feed, paste the RSS feed URL — a preview of the latest items loads as soon as the address is valid. For a news app, pick a News source from the list (for example a world, business or sports feed from a major publisher), or choose Custom feed URL… and paste your own.",
          },
          {
            type: "p",
            text: "Optionally type a Source title to replace the feed’s own name, and — if the feed tags its articles — use Filter by category to show only one category.",
          },
        ],
        screenshot: {
          src: "apps/rss-feed/02.png",
          alt: "The RSS Feed editor with Feed name, RSS feed URL, Source title and zone sizes",
          caption: "Paste the feed address; the preview loads the latest items.",
          todo: "New RSS Feed editor: Feed name, RSS feed URL, Source title, Zone size grid, Display mode, Card layout.",
        },
      },
      {
        title: "Set the layout and style",
        blocks: [
          {
            type: "ul",
            items: [
              "Display mode — Cards (full-screen rotating cards, great for main zones) or Ticker (a scrolling strip, great for footers). Click a Zone size (Full HD, Wide Banner, Lower Third, Square, Portrait or Half) to preview it and set a different mode for that zone size.",
              "Card layout — Broadcast, Cinematic, Modern or Minimal.",
              "Text direction — LTR (English) or RTL (Arabic).",
              "Number of items to show, plus Seconds per card (cards) or Scroll speed (ticker).",
              "Show — Image, Description, Date and Source; and an optional QR code for each article, placed in any corner.",
              "Colors (Background, Accent, Text) and Refresh every (5 minutes to 6 hours).",
            ],
          },
          cachedNote,
        ],
      },
      assignStep,
    ],
    related: [
      { label: "Google Alerts", to: "/apps/google-alerts" },
      { label: "Noticeboard", to: "/apps/noticeboard" },
    ],
    keywords: ["rss", "feed", "news", "headlines", "ticker", "headline news", "sports news", "technology news", "business news", "entertainment news", "natural world news"],
  },

  {
    slug: "google-alerts",
    category: "apps",
    route: "/apps/google-alerts",
    title: "Google Alerts",
    summary: "Turn a Google Alerts feed into live on-screen headlines.",
    intro:
      "The Google Alerts app shows fresh mentions of a topic you track — your brand, a competitor, or an industry keyword — by reading the RSS feed that Google Alerts creates for you.",
    readingTime: 2,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Google Alerts is in the Live Feeds category.",
          },
        ],
        screenshot: {
          src: "apps/google-alerts/01.png",
          alt: "The App Store filtered to the Google Alerts app",
          todo: "App Store tab, search “Google Alerts”, card visible.",
        },
      },
      {
        title: "Paste your Google Alert feed URL",
        blocks: [
          {
            type: "ol",
            items: [
              "Create an alert at google.com/alerts and open its settings.",
              "Set Deliver to: RSS feed, then copy the feed link Google gives you.",
              "Back in PulsePanels, click Google Alerts and paste the link into Google Alert feed URL.",
            ],
          },
          {
            type: "p",
            text: "Google Alerts always uses clean full-screen cards, so there’s no layout to choose. You can still set the Text direction, the Number of items to show, Seconds per card, what to Show (Description, Date, Source), the Colors and how often to refresh.",
          },
          {
            type: "note",
            tone: "tip",
            text: "You need to be signed in to Google to see the RSS delivery option and copy the feed link.",
          },
          cachedNote,
        ],
        screenshot: {
          src: "apps/google-alerts/02.png",
          alt: "The Google Alerts editor with the Google Alert feed URL field and instructions",
          caption: "Paste the RSS link from your Google Alert.",
          todo: "Google Alerts editor: Google Alert feed URL with the how-to hint, Text direction, Number of items, Seconds per card, Show, Colors.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "RSS Feed & news apps", to: "/apps/rss-feed" },
      { label: "Social & Reviews", to: "/apps/social-reviews" },
    ],
    keywords: ["google alerts", "rss", "mentions", "brand", "news", "monitoring"],
  },

  {
    slug: "currencies",
    category: "apps",
    route: "/apps/currencies",
    title: "Currencies",
    summary: "Live exchange rates for the currencies you choose.",
    intro:
      "The Currencies app shows live exchange rates against a base currency you pick — as a full board or a scrolling ticker, with optional buy and sell prices. Handy for hotels, exchange bureaus, banks, and travel lobbies.",
    readingTime: 3,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Currencies is in the Live Feeds category.",
          },
        ],
        screenshot: {
          src: "apps/currencies/01.png",
          alt: "The App Store filtered to the Currencies app",
          todo: "App Store tab, search “Currencies”, card visible.",
        },
      },
      {
        title: "Pick the base and the currencies to show",
        blocks: [
          {
            type: "p",
            text: "Click Currencies. Add an optional Title, choose the Base currency everything is quoted against, and tick the Currencies to show. Arrange them in the Selected order list by dragging or with the arrows.",
          },
          {
            type: "p",
            text: "Then set the Decimals, how often to Refresh (every 15 minutes to every 12 hours), and the Display: Single rate (1 base = …) or Buy & Sell prices with a Spread % around the live mid-market rate. Choose a Sort order, a Layout — List (full board) or Ticker (scrolling strip) with its Ticker speed — and the Background and Font color.",
          },
          cachedNote,
        ],
        screenshot: {
          src: "apps/currencies/02.png",
          alt: "The Currencies editor with a base currency, selected order and currencies to show",
          caption: "Set a base currency, then tick and order the currencies to show.",
          todo: "Currencies editor: Title, Base currency, Selected order list, Currencies to show grid, preview.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "World Clock", to: "/apps/world-clock" },
      { label: "Weather", to: "/apps/weather" },
    ],
    keywords: ["currencies", "exchange rate", "forex", "fx", "buy sell", "ticker", "money"],
  },

  {
    slug: "transport",
    category: "apps",
    route: "/apps/transport",
    title: "Transport",
    summary: "Nearby bus, metro, train and tram stations with live route badges.",
    intro:
      "The Transport app shows the public-transport stations near a location you choose — buses, metro, trains and trams — with live route badges, so people can see how to get around at a glance.",
    readingTime: 2,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Transport is in the Live Feeds category.",
          },
        ],
        screenshot: {
          src: "apps/transport/01.png",
          alt: "The App Store search results for “Transport”",
          todo: "App Store tab, search “Transport”, Transport card visible.",
        },
      },
      {
        title: "Set the location, radius and transit types",
        blocks: [
          {
            type: "p",
            text: "Click Transport. Under Location, type an address or area and pick a suggestion, or click the map to drop the pin where the screen is. Drag Search radius (100 m to 2 km) — the circle on the map shows the area covered. Tick the Means of transit to include: Buses, Metro / Subway, Trains and Trams.",
          },
          {
            type: "p",
            text: "Then choose a Theme (Dark or Light), the Transition speed, and the Station name language (names fall back to the local name when no translation exists).",
          },
          onlineNote,
        ],
        screenshot: {
          src: "apps/transport/02.png",
          alt: "The Transport editor with Location map, Search radius and Means of transit",
          caption: "Drop the pin, set the radius, and tick the transit types.",
          todo: "Transport editor: Location search + map with a pin and radius circle (London), Search radius slider, Means of transit, Theme, Transition speed, Station name language. Needs a Google Maps key that allows the capture host as a referrer (the local demo's key refuses helpdemo.localhost and localhost with RefererNotAllowedMapError, so it is not captured yet); do not centre the map on Doha.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Weather", to: "/apps/weather" },
    ],
    keywords: ["transport", "stations", "bus", "train", "tram", "metro", "transit", "nearby"],
  },

  {
    slug: "live-sources",
    category: "apps",
    route: "/apps/live-sources",
    title: "Live Sources",
    summary: "Play a live stream — IPTV, a camera, or a broadcast feed.",
    intro:
      "The Live Sources app plays a live video feed on a screen — an IPTV channel, a network camera, a multicast stream, or a professional broadcast feed — so you can show live TV, a security view, or an event feed on your signage. It picks the right playback method for each player automatically.",
    readingTime: 3,
    status: "draft",
    group: "Live Feeds",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Live Sources is in the Live Feeds category.",
          },
        ],
        screenshot: {
          src: "apps/live-sources/01.png",
          alt: "The App Store filtered to the Live Sources app",
          todo: "App Store tab, search “Live Sources”, card visible.",
        },
      },
      {
        title: "Add the stream",
        blocks: [
          {
            type: "p",
            text: "Click Live Sources. Choose the Source type (AUTO, HLS, DASH, RTSP, HTTP_STREAM, UDP, RTP or MULTICAST) and the Transport (AUTO, TCP, UDP or MULTICAST), then paste the Stream URL — for example an .m3u8 address or an rtsp:// link. The preview starts playing (muted) as soon as the address works.",
          },
          {
            type: "p",
            text: "Optionally add a Backup URL and set Fallback after (sec) — how long the screen waits on a failing stream before switching. Pick the Aspect (FIT, FILL, CROP or STRETCH) and untick Audio enabled if the stream should play silently.",
          },
          {
            type: "note",
            tone: "info",
            text: "HLS is the most widely supported stream type. Cameras that need a username and password are routed through a PulsePanels Stream Gateway, and HDMI input depends on the player hardware — contact support to set either up.",
          },
          onlineNote,
        ],
        screenshot: {
          src: "apps/live-sources/02.png",
          alt: "The Live Sources editor with Source type, Transport, Stream URL, Backup URL, Aspect and Fallback after",
          caption: "Choose the source type, paste the stream URL, and set a backup.",
          todo: "Create: Live Source editor with the “Needs internet” note, Source type, Transport, Stream URL, Backup URL, Aspect, Fallback after, Audio enabled; preview playing a test HLS stream if possible.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "YouTube Live", to: "/apps/youtube-live" },
      { label: "Website URL", to: "/apps/website-url" },
    ],
    keywords: ["live", "stream", "hls", "rtsp", "dash", "multicast", "iptv", "camera", "stream gateway", "video feed"],
  },

  // ── Communication ──────────────────────────────────────────────────────────
  {
    slug: "digital-menu-board",
    category: "apps",
    route: "/apps/digital-menu-board",
    title: "Digital Menu Board",
    summary: "Build a menu with pages, sections, items, prices, and photos.",
    intro:
      "The Digital Menu Board app builds a professional menu — pages, sections, items, prices, and photos — for cafés, restaurants, and counters. Update a price once and every screen showing the menu changes with it.",
    readingTime: 4,
    status: "draft",
    group: "Communication",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Digital Menu Board is in the Communication category.",
          },
        ],
        screenshot: {
          src: "apps/digital-menu-board/01.png",
          alt: "The App Store filtered to the Digital Menu Board app",
          todo: "App Store tab, search “Digital Menu Board”, card visible.",
        },
      },
      {
        title: "Build your menu",
        blocks: [
          {
            type: "p",
            text: "Click Digital Menu Board. Under Pages, each page plays in turn for the number of seconds you set; use + Add page for more (for example Breakfast, Lunch, Drinks), and the eye icon to hide a page without deleting it. For the selected page, set a Page heading, an optional Page subtitle, and the number of Columns.",
          },
          {
            type: "p",
            text: "Under Menu sections, use + Add section (for example Starters, Mains) and + Add item. Each item has a name, a price, an optional description, calories and a tag, and a photo from your media library.",
          },
          {
            type: "note",
            tone: "tip",
            text: "In a hurry? Use AI Studio’s Menu mode to draft a whole menu, then click Create menu board to finish it here.",
          },
        ],
        screenshot: {
          src: "apps/digital-menu-board/02.png",
          alt: "The Menu Board editor with display options, pages and a live menu preview",
          caption: "Add pages, sections and items — the preview updates as you type.",
          todo: "Configure: Menu Board editor showing Pages list, Menu sections with items, and the preview.",
        },
      },
      {
        title: "Style it",
        blocks: [
          {
            type: "p",
            text: "Choose whether to show Calories, set the Currency symbol and Currency position, and the size of Item images. Pick a Base font and Font size (Auto fills the screen), and optionally override fonts per element under Element fonts. Finally set the Background, an Accent color for headings and prices, and the Font color.",
          },
          offlineNote,
        ],
      },
      assignStep,
    ],
    related: [
      { label: "Promo Wall / Campaign Posts", to: "/apps/promo-wall" },
      { label: "How apps work", to: "/apps/add-an-app" },
    ],
    keywords: ["menu", "menu board", "restaurant", "cafe", "prices", "food", "calories", "ai"],
  },

  {
    slug: "noticeboard",
    category: "apps",
    route: "/apps/noticeboard",
    title: "Noticeboard",
    summary: "Post rotating announcements and notices.",
    intro:
      "The Noticeboard app shows up to 20 notices that rotate on screen — great for staff rooms, receptions, schools, and communal spaces where messages need to be seen.",
    readingTime: 3,
    status: "draft",
    group: "Communication",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Noticeboard is in the Communication category.",
          },
        ],
        screenshot: {
          src: "apps/noticeboard/01.png",
          alt: "The App Store filtered to the Noticeboard app",
          todo: "App Store tab, search “Noticeboard”, card visible.",
        },
      },
      {
        title: "Set up the board",
        blocks: [
          {
            type: "p",
            text: "Click Noticeboard. Give it a Noticeboard name, pick the Zone size it will play in (Full HD, Wide Banner, Lower Third, Square, Portrait or Half — the preview scales to match), and choose the Slide transition: Fade or Slide.",
          },
        ],
        screenshot: {
          src: "apps/noticeboard/02.png",
          alt: "The Create Noticeboard editor with name, zone sizes, transition and the first notice",
          caption: "Name the board, pick a zone size and transition, then write notices.",
          todo: "Create Noticeboard editor: name, Zone size grid, Slide transition, Notices list, notice editor, preview.",
        },
      },
      {
        title: "Write your notices",
        blocks: [
          {
            type: "p",
            text: "Use + Add to add a notice and the arrows to reorder. For each notice, write a Title and optional Body text (the smiley button inserts emoji), and style each with its font, size, weight and alignment. Choose a Background — Solid color or Background image with an opacity slider — a Text color, how many seconds it shows (Duration), and a Template: Centered, Headline, Left Align or Split.",
          },
          {
            type: "p",
            text: "You can also edit directly in the preview: click text to edit it, drag the bar to move a text box, and drag the corner handle to resize it. Reset layout to template puts things back.",
          },
          {
            type: "note",
            tone: "tip",
            text: "AI Studio’s Noticeboard mode can draft a set of notices for you — click Create noticeboard to open them here.",
          },
          cachedNote,
        ],
      },
      {
        title: "Save it and put it on a screen",
        blocks: [
          {
            type: "p",
            text: "Click Create Noticeboard (or Save Changes when editing). The board is saved to My Apps; assign it to a screen with Set content, or add it to a playlist.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "RSS Feed & news apps", to: "/apps/rss-feed" },
      { label: "Ask a Question", to: "/apps/ask-a-question" },
    ],
    keywords: ["noticeboard", "notices", "announcements", "board", "bulletin", "messages", "emoji"],
  },

  {
    slug: "ask-a-question",
    category: "apps",
    route: "/apps/ask-a-question",
    title: "Ask a Question",
    summary: "Show a question with a QR code viewers scan to respond.",
    intro:
      "The Ask a Question app puts a question on screen with a QR code that viewers scan to respond by email or through a web form — a simple way to gather feedback, sign-ups, or opinions.",
    readingTime: 2,
    status: "draft",
    group: "Communication",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Ask a Question is in the Communication category.",
          },
        ],
        screenshot: {
          src: "apps/ask-a-question/01.png",
          alt: "The App Store filtered to the Ask a Question app",
          todo: "App Store tab, search “Ask a Question”, card visible.",
        },
      },
      {
        title: "Write the question and choose how people respond",
        blocks: [
          {
            type: "p",
            text: "Click Ask a Question and type your Question. Under How should people respond?, choose Email and enter the Email address replies go to, or Web form and paste the Web form URL (for example a Microsoft or Google form). The QR code on screen is generated from that address.",
          },
          {
            type: "p",
            text: "Style it with a Background (Default gradient, Image or Solid color), a Font and a Font color (leave blank for white).",
          },
          offlineNote,
        ],
        screenshot: {
          src: "apps/ask-a-question/02.png",
          alt: "The Ask a Question editor with the question, response method, email address and styling",
          caption: "Write the question, pick Email or Web form, and style it.",
          todo: "New Ask a Question editor: Question, How should people respond?, Email address, Background, Font, Font color; preview with a QR code (use an @example.com address).",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Follow Us", to: "/apps/follow-us" },
      { label: "Noticeboard", to: "/apps/noticeboard" },
    ],
    keywords: ["question", "qr code", "responses", "feedback", "survey", "web form", "email"],
  },

  {
    slug: "social-reviews",
    category: "apps",
    route: "/apps/social-reviews",
    title: "Social & Reviews",
    summary: "Show approved customer reviews and social posts.",
    intro:
      "The Social & Reviews app showcases your best reviews and social posts on screen — reviews you type in, Google reviews, and Instagram or Facebook posts — with moderation, so only items you approve are ever shown.",
    readingTime: 4,
    status: "draft",
    group: "Communication",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Social & Reviews is in the Communication category.",
          },
        ],
        screenshot: {
          src: "apps/social-reviews/01.png",
          alt: "The App Store filtered to the Social & Reviews app",
          todo: "App Store tab, search “Social & Reviews”, card visible.",
        },
      },
      {
        title: "Choose the source, layout and theme",
        blocks: [
          {
            type: "p",
            text: "Click Social & Reviews. Set the On-screen title, then the Source type: Manual Reviews, Google Reviews, Instagram Business (auto), or Instagram / Facebook Posts (manual URLs). Choose a Layout — Review Carousel, Social Grid, or Review + QR Code — and the Refresh interval, Orientation, which sources to Display on screen, and a Fallback message for when nothing is approved.",
          },
          {
            type: "p",
            text: "Under Theme, pick Background, Text and Accent colours and a Font size, optionally upload a logo, and turn on Show QR code with a QR link URL and QR caption (for example a link to your review page).",
          },
        ],
        screenshot: {
          src: "apps/social-reviews/02.png",
          alt: "The Social & Reviews editor with source type, layout, theme and QR settings",
          caption: "Pick a source and layout, then style the theme.",
          todo: "Create: Social & Reviews editor: On-screen title, Source type, Layout, Refresh interval, Orientation, Theme with QR fields; preview (approved items only).",
        },
      },
      {
        title: "Add reviews and posts, then approve them",
        blocks: [
          {
            type: "ul",
            items: [
              "Manual reviews — under Add a manual review, enter the Reviewer name, Rating, Review text, Source label and optional date and avatar, then click Add review.",
              "Google reviews — enter the Business name and Place ID, set a Minimum rating and Maximum reviews, choose whether to show reviewer names and dates, and click Sync from Google. New reviews arrive each time you sync; Auto-approve reviews ≥ min rating approves them for you.",
              "Instagram / Facebook posts — paste a public post URL and click Add post URL.",
            ],
          },
          {
            type: "p",
            text: "Everything lands in the Moderation list. Mark each item Approved, Pending or Rejected — only approved items appear on screen and in the preview.",
          },
          onlineNote,
        ],
      },
      assignStep,
    ],
    related: [
      { label: "Follow Us", to: "/apps/follow-us" },
      { label: "Google Alerts", to: "/apps/google-alerts" },
    ],
    keywords: ["reviews", "google reviews", "place id", "social", "instagram", "facebook", "ratings", "testimonials", "moderation"],
  },

  {
    slug: "follow-us",
    category: "apps",
    route: "/apps/follow-us",
    title: "Follow Us",
    summary: "Show your social links with QR codes viewers can scan.",
    intro:
      "The Follow Us app displays your social profiles with QR codes that viewers scan to follow you instantly — an easy way to grow your audience from any screen. Each platform uses its own brand colours automatically.",
    readingTime: 2,
    status: "draft",
    group: "Communication",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Follow Us is in the Communication category.",
          },
        ],
        screenshot: {
          src: "apps/follow-us/01.png",
          alt: "The App Store filtered to the Follow Us app",
          todo: "App Store tab, search “Follow Us”, card visible.",
        },
      },
      {
        title: "Add your links and pick a layout",
        blocks: [
          {
            type: "p",
            text: "Click Follow Us. Set a Title and Subtitle, then under Social links pick a platform (Instagram, Facebook, TikTok, LinkedIn, X, YouTube, WhatsApp and more) and click + Add platform. For each link, enter the URL and optionally a Display label and Handle; untick On to hide a link without deleting it, and use the arrows to reorder. A QR code is generated for every link.",
          },
          {
            type: "p",
            text: "Choose a Layout — Single QR Focus, Multi-platform Grid, Branding Split or Rotating Carousel — and the Orientation. Under Theme, keep Auto brand colors or switch to Custom theme override, set the Font size and QR size, and choose whether to show icons, handles, URLs and your logo.",
          },
          offlineNote,
        ],
        screenshot: {
          src: "apps/follow-us/02.png",
          alt: "The Follow Us editor with title, layout, theme and social links",
          caption: "Add links — QR codes are generated for you — then pick a layout.",
          todo: "Create: Follow Us editor with 2–3 links added (placeholder handles), Layout, Theme; preview showing QR codes.",
        },
      },
      assignStep,
    ],
    related: [
      { label: "Social & Reviews", to: "/apps/social-reviews" },
      { label: "Ask a Question", to: "/apps/ask-a-question" },
    ],
    keywords: ["follow us", "social", "qr code", "handles", "instagram", "tiktok", "profiles"],
  },

  {
    slug: "promo-wall",
    category: "apps",
    route: "/apps/promo-wall",
    title: "Promo Wall / Campaign Posts",
    summary: "Generate polished promo slides from a simple form.",
    intro:
      "The Promo Wall / Campaign Posts app builds eye-catching promotional slides from a simple form — no design tools needed. Pick a template style, fill in the offer, and PulsePanels designs it. Schedule each promo so it only shows while the offer runs.",
    readingTime: 3,
    status: "draft",
    group: "Communication",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open the App Store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and stay on the App Store tab. Promo Wall / Campaign Posts is in the Communication category.",
          },
        ],
        screenshot: {
          src: "apps/promo-wall/01.png",
          alt: "The App Store filtered to the Promo Wall / Campaign Posts app",
          todo: "App Store tab, search “Promo Wall”, card visible.",
        },
      },
      {
        title: "Add promos from a template style",
        blocks: [
          {
            type: "p",
            text: "Click Promo Wall / Campaign Posts. Under Campaign promos, pick a template style — Food, Hotel / Spa, Event, Fitness / Gym, Retail / Flash Sale, Recruitment / Hiring, Registration / Open Day, or Social / Follow / CTA — and click + Add promo. Fill in the fields for that style (titles, description, price, badge, call-to-action and so on), a QR link, and a Background image from your library or a solid colour.",
          },
          {
            type: "p",
            text: "Set each promo’s Status (Active, or Draft to hide it) and optional Start and End / expiry dates — a promo only shows between them, and its badge shows Live or Hidden.",
          },
        ],
        screenshot: {
          src: "apps/promo-wall/02.png",
          alt: "The Promo Wall editor with a Food Promo and a live preview",
          caption: "Pick a style, fill in the offer, and PulsePanels designs the slide.",
          todo: "Create: Promo Wall editor with one Food Promo (sample data), Layout, Orientation, Rotation, preview.",
        },
      },
      {
        title: "Choose the layout",
        blocks: [
          {
            type: "p",
            text: "Choose a Layout — Carousel, Grid, Split, Full-screen or Story (portrait) — the Orientation, and Rotation (seconds) between promos. Tick Show badge, Show CTA and Show QR to include those elements, and set a Fallback message for when no promo is active.",
          },
          offlineNote,
        ],
      },
      assignStep,
    ],
    related: [
      { label: "Digital Menu Board", to: "/apps/digital-menu-board" },
      { label: "Noticeboard", to: "/apps/noticeboard" },
    ],
    keywords: ["promo", "promotion", "campaign", "campaign posts", "poster", "offer", "sale", "marketing", "schedule"],
  },
];
